<script setup lang="ts">
import LeEmail from "@dsk/icons/LeEmail.vue";
import LeFacebook from "@dsk/icons/LeFacebook.vue";
import LeInstagram from "@dsk/icons/LeInstagram.vue";
import LeLinkedin from "@dsk/icons/LeLinkedin.vue";
import LePhone from "@dsk/icons/LePhone.vue";
import LeYoutube from "@dsk/icons/LeYoutube.vue";
import { useI18n } from "vue-i18n";
import { RouterLink } from "vue-router";

import { useSiteFooter } from "@/composables";
import { config } from "@/config";

import LeFooterDisclosure from "./LeFooterDisclosure.vue";
import LeFooterLanguageSelector from "./LeFooterLanguageSelector.vue";

const { t } = useI18n();
const { languageSelectorOptions, changeSiteLanguage, trackFooterButtonClick } = useSiteFooter("expanded");

const { ordbogen } = config;
</script>

<template>
  <div class="le-expanded-footer-mobile">
    <div class="le-expanded-footer-mobile__top">
      <div class="le-expanded-footer-mobile__disclosure-wrapper">
        <LeFooterDisclosure :title="t('le-expanded-footer.navigation.title')">
          <RouterLink
            :to="{ name: 'home' }"
            class="le-expanded-footer-mobile__disclosure-link"
            @click="trackFooterButtonClick('Main page')"
            >{{ t("le-expanded-footer.navigation.main") }}</RouterLink
          >
          <RouterLink
            :to="{ name: 'subscription' }"
            class="le-expanded-footer-mobile__disclosure-link"
            @click="trackFooterButtonClick('Sign-up and prices')"
          >
            {{ t("le-expanded-footer.navigation.pricing") }}
          </RouterLink>
          <RouterLink
            :to="{ name: 'about-dictionaries' }"
            class="le-expanded-footer-mobile__disclosure-link"
            @click="trackFooterButtonClick('About the dictionaries')"
          >
            {{ t("le-expanded-footer.navigation.about") }}
          </RouterLink>
          <RouterLink
            :to="{ name: 'download' }"
            class="le-expanded-footer-mobile__disclosure-link"
            @click="trackFooterButtonClick('Download')"
          >
            {{ t("le-expanded-footer.navigation.download") }}
          </RouterLink>
          <RouterLink
            :to="{ name: 'download', hash: '#desktop-app' }"
            class="le-expanded-footer-mobile__disclosure-link"
            @click="trackFooterButtonClick('Ordbogsprogrammet')"
          >
            {{ t("le-expanded-footer.navigation.program") }}
          </RouterLink>
          <RouterLink
            :to="{ name: 'support' }"
            class="le-expanded-footer-mobile__disclosure-link"
            @click="trackFooterButtonClick('Help')"
          >
            {{ t("le-expanded-footer.navigation.support") }}
          </RouterLink>
          <a
            href="https://drift.ordbogen.com/"
            target="_blank"
            class="le-expanded-footer-mobile__disclosure-link"
            @click="trackFooterButtonClick('Operational status')"
          >
            {{ t("le-expanded-footer.navigation.status") }}
          </a>
        </LeFooterDisclosure>
        <LeFooterDisclosure :title="t('le-expanded-footer.misc.title')">
          <a
            href="https://careers.ordbogen.com/"
            class="le-expanded-footer-mobile__disclosure-link"
            @click="trackFooterButtonClick('Career opportunities')"
          >
            {{ t("le-expanded-footer.misc.career") }}
          </a>
          <a
            href="https://abc.ordbogen.com/"
            target="_blank"
            class="le-expanded-footer-mobile__disclosure-link"
            @click="trackFooterButtonClick('ABC-Ordbogen')"
          >
            {{ t("le-expanded-footer.misc.abc") }}
          </a>
          <a
            href="https://www.grammatip.com/"
            target="_blank"
            class="le-expanded-footer-mobile__disclosure-link"
            @click="trackFooterButtonClick('Grammatip')"
          >
            {{ t("le-expanded-footer.misc.grammatip") }}
          </a>
        </LeFooterDisclosure>
        <LeFooterDisclosure :title="t('le-expanded-footer.legal.title')">
          <RouterLink
            :to="{ name: 'terms' }"
            class="le-expanded-footer-mobile__disclosure-link"
            @click="trackFooterButtonClick('Terms and conditions of sale')"
          >
            {{ t("le-expanded-footer.legal.terms") }}
          </RouterLink>
          <RouterLink
            :to="{ name: 'policies' }"
            class="le-expanded-footer-mobile__disclosure-link"
            @click="trackFooterButtonClick('Cookies and privacy')"
          >
            {{ t("le-expanded-footer.legal.cookie") }}
          </RouterLink>
        </LeFooterDisclosure>
        <LeFooterDisclosure :title="t('le-expanded-footer.contact.title')">
          <a
            :href="`tel:${ordbogen.phone.number}`"
            class="le-expanded-footer-mobile__disclosure-link"
            @click="trackFooterButtonClick('phone number', false)"
            ><LePhone role="presentation" class="le-expanded-footer-mobile__disclosure-link-icon" />
            {{ ordbogen.phone.display }}
          </a>
          <a
            :href="`mailto:${ordbogen.email.support}`"
            class="le-expanded-footer-mobile__disclosure-link"
            @click="trackFooterButtonClick('email address', false)"
            ><LeEmail role="presentation" class="le-expanded-footer-mobile__disclosure-link-icon" />
            {{ ordbogen.email.support }}
          </a>
        </LeFooterDisclosure>
      </div>
      <div class="le-expanded-footer-mobile__social-media">
        <h6 class="le-expanded-footer-mobile__social-media-title">{{ t("le-expanded-footer.socials.title") }}</h6>
        <div class="le-expanded-footer-mobile__socials">
          <a
            href="https://www.facebook.com/ordbogen/"
            :aria-label="t('le-expanded-footer.socials.facebook-label')"
            target="_blank"
            @click="trackFooterButtonClick('Facebook button', false)"
          >
            <LeFacebook role="presentation" class="le-expanded-footer-mobile__socials-link" />
          </a>
          <a
            href="https://www.instagram.com/ordbogencom/"
            :aria-label="t('le-expanded-footer.socials.instagram-label')"
            target="_blank"
            @click="trackFooterButtonClick('Instagram button', false)"
          >
            <LeInstagram role="presentation" class="le-expanded-footer-mobile__socials-link" />
          </a>
          <a
            href="https://www.linkedin.com/company/ordbogen-a-s/"
            :aria-label="t('le-expanded-footer.socials.linkedin-label')"
            target="_blank"
            @click="trackFooterButtonClick('LinkedIn button', false)"
          >
            <LeLinkedin role="presentation" class="le-expanded-footer-mobile__socials-link" />
          </a>
          <a
            href="https://www.youtube.com/channel/UCnABCeeR5L0i34R4enGrdWw/"
            :aria-label="t('le-expanded-footer.socials.youtube-label')"
            target="_blank"
            @click="trackFooterButtonClick('Youtube button', false)"
          >
            <LeYoutube role="presentation" class="le-expanded-footer-mobile__socials-link" />
          </a>
        </div>
      </div>
      <div class="le-expanded-footer-mobile__language-selector">
        <LeFooterLanguageSelector :options="languageSelectorOptions" @change="changeSiteLanguage($event)" />
      </div>
      <div class="le-expanded-footer-mobile__gdpr">
        <p class="le-expanded-footer-mobile__gdpr-text">{{ t("le-expanded-footer.gdpr") }}</p>
        <img
          src="@/assets/img/gdpr/gt-badge.png?halfSize"
          srcset="@/assets/img/gdpr/gt-badge.png 2x"
          alt="Grant Thornton ISAE 3000 GDPR 2024"
          class="le-expanded-footer-desktop__gdpr-badge"
        />
      </div>
    </div>
    <div class="le-expanded-footer-mobile__bottom">
      <p class="mb-0">{{ t("le-expanded-footer.bottom.short", { year: new Date().getFullYear() }) }}</p>
    </div>
  </div>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all.less";

.le-expanded-footer-mobile {
  background: @colors[@secondary][500];

  &__language-selector {
    max-width: 188px;
    margin-top: 36px;
    padding: 0 24px;
  }

  &__disclosure-wrapper {
    display: grid;
    grid-template-columns: 1fr;
  }

  &__disclosure-link {
    display: flex;
    align-items: center;
    padding: 4px 12px;

    &-icon {
      margin-right: 8px;
    }

    &.focus-visible {
      box-shadow: 0px 0px 0px 4px rgba(255, 255, 255, 0.4);
      color: inherit;
    }

    &:hover {
      color: inherit;
      text-decoration: underline;
    }
  }

  &__social-media {
    padding: 0 24px;
  }

  &__social-media-title {
    margin-top: 36px;
    margin-bottom: 16px;
    font-family: @font-family-heading;
    font-size: 1.25rem;
  }

  &__socials {
    display: flex;

    &-link {
      margin-right: 8px;
      color: @colors[@neutrals][400];
    }
  }

  &__gdpr {
    padding: 32px 24px 16px 24px;
  }

  &__gdpr-text {
    color: black;
    font-size: 0.75rem;
    font-style: italic;
  }

  &__gdpr-badge {
    max-width: 204px;
  }

  &__bottom {
    padding: 16px 24px;
    background-color: @colors[@primary][700];
    color: @colors[@neutrals][100];
  }
}
</style>
